// Индикатор агента: работает / свободен / лимит до HH:MM (не сегодня — с днём) и сколько ждёт вас (то же число, что у «Решений»),
// плюс шкалы окон подписки — только тех, что заполнены больше чем на 75% (иначе это шум: хватит и индикатора).
import { windowLabel } from '@autoaff/protocol';
import { Meter, till } from '@autoaff/ui';
import type { AgentStatus } from '../api';
import { chatStore } from '../chat/store';
import { useAgentStatus } from '../streams';
import { useWaitingTotal } from './waiting';

export function agentState(
  s: AgentStatus | null,
  online: boolean,
  /** «Ждёт вас» — одно число на всю платформу (shell/waiting.ts); null — ещё не пришло. */
  waiting: number | null = null,
): { tone: '' | 'busy' | 'off' | 'warn' | 'danger'; title: string; hint: string } {
  if (!online || !s) return { tone: 'off', title: 'Нет связи с платформой', hint: 'Переподключаюсь…' };
  if (!s.runner) return { tone: 'off', title: 'Агент недоступен', hint: 'Сервис запуска не подключён' };
  if (s.access?.state === 'lost')
    return { tone: 'danger', title: 'Нет доступа к Claude', hint: 'Обновите токен в настройках' };
  const w = waiting ?? 0;
  const ask = (fallback: string) => (w > 0 ? `Ждёт вас: ${w}` : fallback);
  const blocked = s.subscription.find((x) => x.status === 'rejected' || x.utilization >= 1);
  if (blocked?.resetsAt)
    return {
      tone: 'warn',
      title: `Лимит до ${till(blocked.resetsAt)}`,
      hint: ask('Фоновые задачи отложены'),
    };
  // Агент остановился посреди работы и ждёт разрешения — это важнее всего остального
  if (s.pendingPermissions > 0)
    return { tone: 'busy', title: 'Ждёт вашего решения', hint: ask('Агент ждёт разрешения') };
  if (s.queue?.limitUntil)
    return {
      tone: 'warn',
      title: `Лимит до ${till(s.queue.limitUntil)}`,
      hint: ask('Работа по расписанию ждёт сброса окна'),
    };
  if (s.working > 0 || (s.queue?.background ?? 0) > 0)
    return {
      tone: 'busy',
      title: 'Агент работает',
      hint: ask(s.working > 0 ? 'Можно дописать или остановить' : 'Работа по расписанию'),
    };
  if (w > 0) return { tone: '', title: 'Агент свободен', hint: `Ждёт вас: ${w}` };
  return {
    tone: '',
    title: 'Агент свободен',
    hint: s.queue?.waiting ? `В очереди: ${s.queue.waiting}` : 'Работает по расписанию и по событиям',
  };
}

/** Шкала окна подписки в меню — только когда окно заполнено больше чем на 75%. */
const NEAR_LIMIT = 0.75;
const showInSidebar = (w: { utilization: number }) => w.utilization > NEAR_LIMIT;

export function AgentIndicator() {
  const { status, online } = useAgentStatus();
  const waiting = useWaitingTotal();
  const st = agentState(status, online, waiting);
  const windows = (status?.subscription ?? []).filter(showInSidebar);
  return (
    <div className="side-foot">
      <button
        type="button"
        className="agent-ind"
        onClick={() => chatStore.open()}
        title="Открыть чат с агентом"
      >
        <span className={`status-dot ${st.tone}`} />
        <div>
          <b>{st.title}</b>
          <span>{st.hint}</span>
        </div>
      </button>
      {windows.length > 0 && (
        <div className="lims">
          {windows.map((w) => (
            <Row key={w.kind} label={windowLabel(w.kind, true)} value={w.utilization} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <>
      <span>{label}</span>
      <Meter
        value={value}
        label={label}
        tone={value >= 0.9 ? 'danger' : value >= 0.75 ? 'warn' : undefined}
      />
      <span className="tnum">{Math.round(value * 100)}%</span>
    </>
  );
}
